import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  FulfillmentGroupReassignmentReason,
  FulfillmentGroupReassignmentReasonLabels,
} from "@redotech/redo-fulfillment-group-common/reassignment-reason";
import MarkerPinIcon from "@redotech/redo-icons/marker-pin-01.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import {
  coverageOf,
  PICK_META,
  ReassignmentLocationOption,
} from "./move-to-fulfillment-center-modal";
import * as styles from "./move-to-fulfillment-center-modal.module.css";

const REASON_OPTIONS = Object.values(FulfillmentGroupReassignmentReason);

function rankOf(location: ReassignmentLocationOption): number {
  if (location.ineligibleReason) {
    return 3;
  }
  const { pickability } = coverageOf(location.items);
  return pickability === "all" ? 0 : pickability === "partial" ? 1 : 2;
}

export const AssignToMerchantModal = memo(function AssignToMerchantModal({
  isOpen,
  fulfillmentGroup,
  locations,
  warehouseName,
  pending,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  fulfillmentGroup: FulfillmentGroupData | null;
  /** null while destinations are loading; empty = assign without a move. */
  locations: readonly ReassignmentLocationOption[] | null;
  warehouseName: string;
  pending: boolean;
  onClose: () => void;
  onConfirm: (input: {
    reason: FulfillmentGroupReassignmentReason;
    note: string;
    externalLocationId?: string;
  }) => void;
}) {
  const [reason, setReason] =
    useState<FulfillmentGroupReassignmentReason | null>(null);
  const [note, setNote] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setReason(null);
      setNote("");
      setQuery("");
      setSelectedId(null);
    }
  }, [isOpen]);

  const sorted = useMemo(() => {
    if (!locations) {
      return [];
    }
    return [...locations].sort(
      (a, b) => rankOf(a) - rankOf(b) || a.name.localeCompare(b.name),
    );
  }, [locations]);

  const anyFullyPickable = sorted.some((l) => rankOf(l) === 0);
  const noEligibleLocation =
    sorted.length > 0 && sorted.every((l) => !!l.ineligibleReason);

  // Preselect the best destination when it fully covers the order.
  useEffect(() => {
    if (isOpen && !selectedId && sorted.length > 0 && anyFullyPickable) {
      setSelectedId(sorted[0]!.externalLocationId);
    }
  }, [isOpen, selectedId, sorted, anyFullyPickable]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((l) => !q || l.name.toLowerCase().includes(q));
  }, [sorted, query]);

  const selected = useMemo(
    () => sorted.find((l) => l.externalLocationId === selectedId) ?? null,
    [sorted, selectedId],
  );
  const selectedCoverage = selected ? coverageOf(selected.items) : null;

  const destinationLabel = fulfillmentGroup?.teamInfo?.name ?? "Merchant";

  const totalUnits =
    sorted[0]?.items.reduce((sum, item) => sum + item.needed, 0) ?? 0;
  const orderLabel = fulfillmentGroup?.orders?.[0]?.name ?? "Order";
  const subtitle =
    totalUnits > 0
      ? `${orderLabel} · ${totalUnits} unit${totalUnits === 1 ? "" : "s"}`
      : orderLabel;

  const requiresLocation = !!locations && locations.length > 0;
  const canConfirm =
    !pending &&
    !!fulfillmentGroup &&
    !!locations &&
    !!reason &&
    (!requiresLocation || (!!selected && !selected.ineligibleReason));

  return (
    <RedoModal
      hideFooter
      isOpen={isOpen}
      modalSize="lg"
      noPadding
      onModalCloseRequested={onClose}
      subtitle={subtitle}
      title={`Assign to ${destinationLabel}`}
    >
      <Flex className={styles.content}>
        <Flex className={styles.leftPanel} dir="column">
          <Flex dir="column" gap="md" px="xl" py="md">
            <RedoSingleSelectDropdownInput
              label="Reason"
              options={REASON_OPTIONS.map((value) => ({
                type: "text" as const,
                id: value,
                text: FulfillmentGroupReassignmentReasonLabels[value],
                value,
              }))}
              optionSelected={(args) => {
                setReason(args.item.value);
              }}
              placeholder="Select reason"
              selectedItem={
                reason
                  ? {
                      type: "text",
                      id: reason,
                      text: FulfillmentGroupReassignmentReasonLabels[reason],
                      value: reason,
                    }
                  : undefined
              }
            />
            <RedoTextareaInputField
              autoExpand
              label="Note (optional)"
              placeholder="Add note for the merchant ..."
              setValue={setNote}
              value={note}
            />
          </Flex>
          <Divider />
          {!locations ? (
            <Flex dir="column" gap="md" px="xl" py="md">
              <Text fontSize="sm" fontWeight="semibold">
                Destination location
              </Text>
              <AnimatedSkeletonBlock height="36px" width="100%" />
              <AnimatedSkeletonBlock height="36px" width="100%" />
              <AnimatedSkeletonBlock height="36px" width="100%" />
            </Flex>
          ) : !requiresLocation ? (
            <Flex px="xl" py="lg">
              <Text fontSize="xs" textColor="secondary">
                No merchant locations available — orders will be assigned
                without a location move.
              </Text>
            </Flex>
          ) : (
            <>
              <Flex align="center" justify="space-between" px="xl" py="md">
                <Text fontSize="sm" fontWeight="semibold">
                  Destination location
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {visible.length} of {locations.length}
                </Text>
              </Flex>
              {noEligibleLocation ? (
                <Flex pb="md" px="xl">
                  <RedoAlert
                    color="error"
                    description="Cross-border orders can only be handed back to a merchant location in the country they ship to."
                    fullWidth
                    size="sm"
                    title="No merchant location in the order's destination country"
                  />
                </Flex>
              ) : (
                !anyFullyPickable && (
                  <Flex pb="md" px="xl">
                    <RedoAlert
                      color="warning"
                      description="You can still assign — unstocked items will need attention at the destination."
                      fullWidth
                      size="sm"
                      title="No merchant location can fully stock this order"
                    />
                  </Flex>
                )
              )}
              {locations.length > 5 && (
                <Flex pb="md" px="xl">
                  <RedoTextInput
                    IconLeading={SearchIcon}
                    placeholder="Search locations"
                    setValue={setQuery}
                    value={query}
                  />
                </Flex>
              )}
              <Flex className={styles.scrollable} dir="column">
                {visible.length === 0 && (
                  <Flex justify="center" p="xl">
                    <Text fontSize="sm" textColor="secondary">
                      {query
                        ? `No locations match "${query}".`
                        : "No eligible locations."}
                    </Text>
                  </Flex>
                )}
                {visible.map((location) => {
                  const active = location.externalLocationId === selectedId;
                  const ineligible = !!location.ineligibleReason;
                  const meta =
                    PICK_META[coverageOf(location.items).pickability];
                  const select = () => {
                    if (!ineligible) {
                      setSelectedId(location.externalLocationId);
                    }
                  };
                  return (
                    <Flex
                      align="center"
                      className={
                        ineligible
                          ? styles.locationRowDisabled
                          : active
                            ? styles.locationRowSelected
                            : styles.locationRow
                      }
                      gap="md"
                      key={location.externalLocationId}
                      onClick={select}
                      px="xl"
                      py="md"
                    >
                      <RedoRadioButton
                        disabled={ineligible}
                        onSelect={select}
                        selected={active}
                      />
                      <Flex className={styles.flexFill} dir="column" gap="none">
                        <Text fontSize="sm" fontWeight="semibold">
                          {location.name}
                        </Text>
                        {location.region && (
                          <Text fontSize="xs" textColor="secondary">
                            {location.region}
                          </Text>
                        )}
                        {location.ineligibleReason && (
                          <Text fontSize="xs" textColor="error">
                            {location.ineligibleReason}
                          </Text>
                        )}
                      </Flex>
                      {ineligible ? (
                        <RedoBadge color="gray" size="sm" text="Ineligible" />
                      ) : (
                        <RedoBadge
                          color={meta.color}
                          size="sm"
                          text={meta.label}
                        />
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            </>
          )}
        </Flex>

        <Flex className={styles.flexFill} dir="column">
          {!selected || !selectedCoverage ? (
            <Flex
              align="center"
              className={styles.emptyState}
              dir="column"
              gap="md"
              justify="center"
              p="2xl"
            >
              <MarkerPinIcon />
              <Text fontSize="sm" textAlign="center" textColor="secondary">
                {requiresLocation
                  ? "Choose a destination to preview available inventory for this order."
                  : "The merchant has no eligible locations to preview."}
              </Text>
            </Flex>
          ) : (
            <>
              <Flex dir="column" gap="xs" px="xl" py="lg">
                <Flex align="center" gap="sm">
                  <MarkerPinIcon />
                  <Text fontSize="md" fontWeight="semibold">
                    {selected.name}
                  </Text>
                </Flex>
                {selected.region && (
                  <Text fontSize="xs" textColor="secondary">
                    {selected.region}
                  </Text>
                )}
                <Flex align="center" gap="md" pt="xs">
                  <RedoBadge
                    color={PICK_META[selectedCoverage.pickability].color}
                    size="sm"
                    text={PICK_META[selectedCoverage.pickability].label}
                  />
                  <Text fontSize="xs" textColor="secondary">
                    {selectedCoverage.coveredCount} of {selected.items.length}{" "}
                    items in stock
                  </Text>
                </Flex>
              </Flex>
              <Divider />
              <Flex className={styles.scrollable} dir="column">
                {selected.items.map((item, i) => {
                  const unknown = item.onHand < 0;
                  return (
                    <div key={item.lineItemId}>
                      {i > 0 && <Divider />}
                      <Flex
                        align="center"
                        gap="md"
                        justify="space-between"
                        px="xl"
                        py="lg"
                      >
                        <Flex
                          align="center"
                          className={styles.flexFill}
                          gap="md"
                        >
                          {item.imageUrl ? (
                            <img
                              alt={item.name}
                              className={styles.itemThumbnail}
                              src={item.imageUrl}
                            />
                          ) : (
                            <div className={styles.itemThumbnail} />
                          )}
                          <Flex dir="column" gap="none">
                            <Text fontSize="sm" fontWeight="medium">
                              {item.name}
                            </Text>
                            <Text fontSize="xs" textColor="secondary">
                              SKU {item.sku} · need {item.needed}
                            </Text>
                          </Flex>
                        </Flex>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          textColor={
                            unknown || item.onHand === 0
                              ? "secondary"
                              : "primary"
                          }
                        >
                          {unknown
                            ? "Not tracked"
                            : item.onHand === 0
                              ? "Out of stock"
                              : `${item.onHand} in stock`}
                        </Text>
                      </Flex>
                    </div>
                  );
                })}
              </Flex>
              <Flex px="xl" py="md">
                <RedoAlert
                  color="gray"
                  description={`Fulfillment orders move to ${selected.name} in Shopify, and reserved stock at ${warehouseName} is released.`}
                  fullWidth
                  size="sm"
                  title="What happens next"
                />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>

      <Flex
        align="center"
        className={styles.footer}
        justify="space-between"
        px="xl"
        py="lg"
      >
        <Text fontSize="xs" textColor="secondary">
          {canConfirm
            ? `Assigning ${orderLabel} back to ${destinationLabel}`
            : !reason
              ? "Select a reason to continue"
              : requiresLocation
                ? "Pick a destination to continue"
                : ""}
        </Text>
        <Flex gap="md">
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
          />
          <RedoButton
            disabled={!canConfirm}
            hierarchy="primary"
            onClick={() =>
              reason &&
              onConfirm({
                reason,
                note: note.trim(),
                externalLocationId: selected?.externalLocationId,
              })
            }
            pending={pending}
            size="md"
            text={`Assign to ${destinationLabel}`}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
